<script setup lang="ts">
import { useLocalStorage } from '@vueuse/core';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch, type CSSProperties } from 'vue';
import IconLucidePin from '~icons/lucide/pin';
import type { MediaRenderableTrack } from '../jitsi/runtime';
import MediaTrackAttachment from './MediaTrackAttachment.vue';

const props = defineProps<{
  activeCameraParticipantCount: number;
  currentRoomName: string;
  screenShareTrack: MediaRenderableTrack | null;
  selfVideoEnabled: boolean;
  videoPanelLayout: 'floating' | 'docked';
  videoPanelVisible: boolean;
  videoTracks: MediaRenderableTrack[];
}>();

const emit = defineEmits<{
  (event: 'close'): void;
  (event: 'toggle-layout'): void;
}>();

const VIDEO_PANEL_FLOATING_STORAGE_KEY = 'convex-rebuild.preferences.video-panel-floating-geometry';
const FLOATING_DEFAULT_WIDTH = 320;
const FLOATING_DEFAULT_HEIGHT = 420;
const FLOATING_MIN_WIDTH = 240;
const FLOATING_MIN_HEIGHT = 180;
const FLOATING_MARGIN = 20;
const FLOATING_HEADER_HEIGHT = 42;
const FLOATING_BODY_HORIZONTAL_PADDING = 24;
const FLOATING_BODY_VERTICAL_PADDING = 24;
const FLOATING_TILE_GAP = 12;
const FLOATING_TILE_MAX_HEIGHT = 220;
const FLOATING_TILE_ASPECT_RATIO = 16 / 9;

type FloatingGeometry = {
  height: number;
  left: number;
  top: number;
  width: number;
};

const panelElement = ref<HTMLElement | null>(null);
const storedFloatingGeometry = useLocalStorage<FloatingGeometry>(VIDEO_PANEL_FLOATING_STORAGE_KEY, {
  height: FLOATING_DEFAULT_HEIGHT,
  left: 0,
  top: 0,
  width: FLOATING_DEFAULT_WIDTH,
});
const floatingPosition = ref({ left: storedFloatingGeometry.value.left, top: storedFloatingGeometry.value.top });
const floatingSize = ref({ height: storedFloatingGeometry.value.height, width: storedFloatingGeometry.value.width });
const panelInitialized = ref(
  storedFloatingGeometry.value.left > 0 ||
    storedFloatingGeometry.value.top > 0 ||
    storedFloatingGeometry.value.width !== FLOATING_DEFAULT_WIDTH ||
    storedFloatingGeometry.value.height !== FLOATING_DEFAULT_HEIGHT,
);

let resizeObserver: ResizeObserver | null = null;
let stopPointerDrag: (() => void) | null = null;

const selfPreviewTrack = computed(() => props.videoTracks.find((track) => track.isLocal) ?? null);
const remoteVideoTracks = computed(() => props.videoTracks.filter((track) => !track.isLocal));

const feedTracks = computed(() => {
  const tracks: MediaRenderableTrack[] = [];

  if (props.selfVideoEnabled && selfPreviewTrack.value) {
    tracks.push(selfPreviewTrack.value);
  }

  if (props.screenShareTrack) {
    tracks.push(props.screenShareTrack);
  }

  tracks.push(...remoteVideoTracks.value);

  return tracks;
});

const gridColumnCount = computed(() => {
  if (props.videoPanelLayout !== 'floating') {
    return 1;
  }

  return floatingSize.value.width >= 520 ? 2 : 1;
});

const maxGridColumnCount = computed(() => Math.min(2, Math.max(1, feedTracks.value.length)));
const maxGridRowCount = computed(() => Math.max(1, Math.ceil(feedTracks.value.length / maxGridColumnCount.value)));

const floatingMaxWidth = computed(() => {
  const contentWidth =
    FLOATING_BODY_HORIZONTAL_PADDING +
    maxGridColumnCount.value * Math.round(FLOATING_TILE_MAX_HEIGHT * FLOATING_TILE_ASPECT_RATIO) +
    Math.max(0, maxGridColumnCount.value - 1) * FLOATING_TILE_GAP;

  return Math.max(FLOATING_MIN_WIDTH, contentWidth);
});

const floatingMaxHeight = computed(() => {
  const contentHeight =
    FLOATING_HEADER_HEIGHT +
    FLOATING_BODY_VERTICAL_PADDING +
    maxGridRowCount.value * FLOATING_TILE_MAX_HEIGHT +
    Math.max(0, maxGridRowCount.value - 1) * FLOATING_TILE_GAP;

  return Math.max(FLOATING_MIN_HEIGHT, contentHeight);
});

const viewportMaxWidth = computed(() => {
  if (typeof window === 'undefined') {
    return floatingMaxWidth.value;
  }

  return Math.max(FLOATING_MIN_WIDTH, window.innerWidth - FLOATING_MARGIN * 2);
});

const viewportMaxHeight = computed(() => {
  if (typeof window === 'undefined') {
    return floatingMaxHeight.value;
  }

  return Math.max(FLOATING_MIN_HEIGHT, window.innerHeight - 56 - FLOATING_MARGIN);
});

const effectiveFloatingMaxWidth = computed(() => Math.min(floatingMaxWidth.value, viewportMaxWidth.value));
const effectiveFloatingMaxHeight = computed(() => Math.min(floatingMaxHeight.value, viewportMaxHeight.value));

const tileHeight = computed(() => {
  if (props.videoPanelLayout !== 'floating') {
    return 180;
  }

  const rows = Math.max(1, Math.ceil(feedTracks.value.length / gridColumnCount.value));
  const availableHeight = floatingSize.value.height - FLOATING_HEADER_HEIGHT - FLOATING_BODY_VERTICAL_PADDING;
  const totalGap = Math.max(0, rows - 1) * FLOATING_TILE_GAP;
  const nextHeight = Math.floor((availableHeight - totalGap) / rows);

  return Math.min(FLOATING_TILE_MAX_HEIGHT, Math.max(96, nextHeight));
});

const panelClasses = computed(() =>
  props.videoPanelLayout === 'docked'
    ? 'flex h-full min-h-0 w-full flex-col overflow-hidden bg-conference-dock'
    : 'fixed z-30 flex flex-col overflow-hidden rounded-lg border border-conference-floating-border bg-conference-floating-surface shadow-[0_18px_40px_rgba(15,23,42,0.22)]',
);

const headerClasses = computed(() =>
  props.videoPanelLayout === 'docked'
    ? 'flex items-center justify-between gap-2 border-b border-conference-line-muted bg-conference-structure px-1.5 py-1 text-white min-w-0'
    : 'flex items-center gap-2 border-b border-conference-floating-divider bg-conference-floating-header px-2 py-2 text-slate-800',
);

const dragHandleClasses = computed(() =>
  props.videoPanelLayout === 'docked'
    ? 'flex min-w-0 flex-1 cursor-grab items-center justify-center rounded-[5px] border border-white/6 bg-white/4 py-1 active:cursor-grabbing'
    : 'flex min-w-0 flex-1 cursor-grab items-center justify-center rounded-[5px] border border-conference-floating-outline bg-white py-1 shadow-[inset_0_1px_0_rgba(255,255,255,0.85)] active:cursor-grabbing',
);

const dragHandleBarClasses = computed(() =>
  props.videoPanelLayout === 'docked'
    ? 'h-1 w-10 rounded-full bg-white/35'
    : 'h-1.5 w-10 rounded-full bg-conference-floating-handle',
);

const bodyClasses = computed(() =>
  props.videoPanelLayout === 'docked'
    ? 'min-h-0 flex-1 overflow-y-auto bg-conference-dock p-2.5'
    : 'min-h-0 flex-1 overflow-y-auto bg-conference-floating-surface p-3',
);

const tileClasses = computed(() =>
  props.videoPanelLayout === 'docked'
    ? 'relative overflow-hidden rounded-[10px] border border-conference-floating-divider bg-slate-950 shadow-[0_10px_22px_rgba(15,23,42,0.12)]'
    : 'relative overflow-hidden rounded-[10px] border border-conference-floating-divider bg-slate-950 shadow-[0_10px_22px_rgba(15,23,42,0.12)]',
);

const labelPillClasses = computed(() =>
  props.videoPanelLayout === 'docked'
    ? 'inline-flex max-w-full items-center rounded-full border border-white/70 bg-white/92 px-2.5 py-1 text-[11px] font-semibold text-slate-800 shadow-[0_8px_18px_rgba(15,23,42,0.16)]'
    : 'inline-flex max-w-full items-center rounded-full border border-white/70 bg-white/92 px-2.5 py-1 text-[11px] font-semibold text-slate-800 shadow-[0_8px_18px_rgba(15,23,42,0.16)]',
);

const emptyStateClasses = computed(() =>
  props.videoPanelLayout === 'docked'
    ? 'flex h-full min-h-30 items-center justify-center rounded-[10px] border border-dashed border-conference-dock-border bg-white px-3 py-4 text-center text-[11px] text-slate-600'
    : 'flex h-full min-h-30 items-center justify-center rounded-[10px] border border-dashed border-conference-floating-divider bg-white px-3 py-4 text-center text-[11px] text-slate-600',
);
const layoutToggleClasses = computed(() =>
  props.videoPanelLayout === 'docked'
    ? 'inline-flex size-5.25 items-center justify-center rounded-[1px] border border-conference-panel-border bg-conference-control-fill text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] transition hover:border-conference-accent-border hover:text-conference-control-highlight focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-conference-accent/25 aria-pressed:border-conference-accent-border aria-pressed:bg-conference-control-pressed aria-pressed:text-conference-control-highlight aria-pressed:shadow-[inset_0_1px_0_rgba(255,255,255,0.04),inset_0_0_0_1px_rgba(0,0,0,0.24)]'
    : 'inline-flex size-5.25 items-center justify-center rounded-[3px] border border-conference-floating-outline bg-white text-slate-500 shadow-[inset_0_1px_0_rgba(255,255,255,0.7)] transition hover:border-conference-floating-outline hover:bg-slate-50 hover:text-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-200 aria-pressed:border-conference-dock-accent aria-pressed:bg-conference-dock aria-pressed:text-conference-dock-text',
);
const floatingCloseButtonClass =
  'inline-flex size-5.25 items-center justify-center rounded-[3px] border border-conference-floating-outline bg-white text-slate-800 shadow-[inset_0_1px_0_rgba(255,255,255,0.7)] transition hover:border-conference-floating-outline hover:bg-slate-50 hover:text-slate-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-200';

const layoutButtonAriaLabel = computed(() =>
  props.videoPanelLayout === 'docked' ? 'Switch video panel to floating layout' : 'Dock video panel to the side',
);
const dockedHeadingLabel = computed(() => `Video - ${props.currentRoomName}`);
const emptyStateLabel = computed(() => (props.activeCameraParticipantCount > 0 ? 'Video is starting...' : 'No one is on video.'));

const panelStyle = computed<CSSProperties | undefined>(() => {
  if (props.videoPanelLayout === 'docked') {
    return undefined;
  }

  return {
    height: `${floatingSize.value.height}px`,
    left: `${floatingPosition.value.left}px`,
    maxHeight: `${effectiveFloatingMaxHeight.value}px`,
    maxWidth: `${effectiveFloatingMaxWidth.value}px`,
    minHeight: `${FLOATING_MIN_HEIGHT}px`,
    minWidth: `${FLOATING_MIN_WIDTH}px`,
    resize: 'both',
    top: `${floatingPosition.value.top}px`,
    width: `${floatingSize.value.width}px`,
  };
});

const feedGridStyle = computed(() => ({
  gridTemplateColumns: `repeat(${gridColumnCount.value}, minmax(0, 1fr))`,
}));

const tileStyle = computed(() => ({
  height: `${tileHeight.value}px`,
}));

function clampPosition(left: number, top: number): { left: number; top: number } {
  if (typeof window === 'undefined') {
    return { left, top };
  }

  const maxLeft = Math.max(FLOATING_MARGIN, window.innerWidth - floatingSize.value.width - FLOATING_MARGIN);
  const maxTop = Math.max(56, window.innerHeight - floatingSize.value.height - FLOATING_MARGIN);

  return {
    left: Math.min(Math.max(FLOATING_MARGIN, left), maxLeft),
    top: Math.min(Math.max(56, top), maxTop),
  };
}

function clampSize(width: number, height: number): { height: number; width: number } {
  return {
    height: Math.min(effectiveFloatingMaxHeight.value, Math.max(FLOATING_MIN_HEIGHT, height)),
    width: Math.min(effectiveFloatingMaxWidth.value, Math.max(FLOATING_MIN_WIDTH, width)),
  };
}

function persistFloatingGeometry(): void {
  storedFloatingGeometry.value = {
    height: floatingSize.value.height,
    left: floatingPosition.value.left,
    top: floatingPosition.value.top,
    width: floatingSize.value.width,
  };
}

function syncFloatingGeometry(): void {
  if (typeof window === 'undefined') {
    return;
  }

  const element = panelElement.value;

  if (element) {
    const nextSize = clampSize(Math.round(element.offsetWidth), Math.round(element.offsetHeight));
    floatingSize.value = nextSize;
  }

  floatingPosition.value = clampPosition(floatingPosition.value.left, floatingPosition.value.top);
  persistFloatingGeometry();
}

function resetFloatingPosition(): void {
  if (typeof window === 'undefined') {
    return;
  }

  floatingSize.value = clampSize(floatingSize.value.width, floatingSize.value.height);
  const nextLeft = window.innerWidth - floatingSize.value.width - FLOATING_MARGIN;
  const nextTop = window.innerHeight - floatingSize.value.height - FLOATING_MARGIN;
  floatingPosition.value = clampPosition(nextLeft, nextTop);
  persistFloatingGeometry();
  panelInitialized.value = true;
}

function stopDragging(): void {
  stopPointerDrag?.();
  stopPointerDrag = null;
}

function startDragging(event: PointerEvent): void {
  if (props.videoPanelLayout !== 'floating') {
    return;
  }

  event.preventDefault();

  const startX = event.clientX;
  const startY = event.clientY;
  const originLeft = floatingPosition.value.left;
  const originTop = floatingPosition.value.top;

  stopDragging();

  const handlePointerMove = (moveEvent: PointerEvent) => {
    const deltaX = moveEvent.clientX - startX;
    const deltaY = moveEvent.clientY - startY;
    floatingPosition.value = clampPosition(originLeft + deltaX, originTop + deltaY);
    persistFloatingGeometry();
  };

  const handlePointerUp = () => {
    stopDragging();
  };

  window.addEventListener('pointermove', handlePointerMove);
  window.addEventListener('pointerup', handlePointerUp, { once: true });
  stopPointerDrag = () => {
    window.removeEventListener('pointermove', handlePointerMove);
    window.removeEventListener('pointerup', handlePointerUp);
  };
}

function observePanelSize(): void {
  resizeObserver?.disconnect();

  if (props.videoPanelLayout !== 'floating' || !panelElement.value || typeof ResizeObserver === 'undefined') {
    return;
  }

  resizeObserver = new ResizeObserver(() => {
    syncFloatingGeometry();
  });
  resizeObserver.observe(panelElement.value);
}

onMounted(() => {
  window.addEventListener('resize', syncFloatingGeometry);
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  stopDragging();
  window.removeEventListener('resize', syncFloatingGeometry);
});

watch(
  () => props.videoPanelVisible,
  async (visible) => {
    if (!visible) {
      stopDragging();
      return;
    }

    await nextTick();

    if (!panelInitialized.value) {
      resetFloatingPosition();
    } else {
      syncFloatingGeometry();
    }

    observePanelSize();
  },
  { immediate: true },
);

watch(
  [effectiveFloatingMaxWidth, effectiveFloatingMaxHeight],
  () => {
    if (props.videoPanelLayout !== 'floating') {
      return;
    }

    const nextSize = clampSize(floatingSize.value.width, floatingSize.value.height);
    floatingSize.value = nextSize;
    floatingPosition.value = clampPosition(floatingPosition.value.left, floatingPosition.value.top);
    persistFloatingGeometry();
  },
  { immediate: true },
);

watch(
  () => props.videoPanelLayout,
  async (layout) => {
    if (layout !== 'floating' || !props.videoPanelVisible) {
      resizeObserver?.disconnect();
      return;
    }

    await nextTick();

    if (!panelInitialized.value) {
      resetFloatingPosition();
    }

    observePanelSize();
    syncFloatingGeometry();
  },
);
</script>

<template>
  <section v-if="videoPanelVisible" ref="panelElement" data-testid="video-panel" :class="panelClasses" :style="panelStyle">
    <header :class="headerClasses">
      <div v-if="props.videoPanelLayout === 'floating'" :class="dragHandleClasses" @pointerdown="startDragging">
        <span aria-hidden="true" :class="dragHandleBarClasses" />
      </div>
      <div v-else class="min-w-0 flex-1">
        <p class="truncate text-[11px] tracking-[0.14em] uppercase">{{ dockedHeadingLabel }}</p>
      </div>
      <div class="flex items-center gap-1.5">
        <button
          type="button"
          :class="layoutToggleClasses"
          :aria-label="layoutButtonAriaLabel"
          :aria-pressed="props.videoPanelLayout === 'docked'"
          @click="emit('toggle-layout')"
        >
          <IconLucidePin aria-hidden="true" class="size-3.5" />
        </button>
        <button
          v-if="props.videoPanelLayout === 'floating'"
          type="button"
          :class="floatingCloseButtonClass"
          aria-label="Close video panel"
          @click="emit('close')"
        >
          <span aria-hidden="true" class="text-[13px] leading-none">×</span>
        </button>
      </div>
    </header>

    <div :class="bodyClasses">
      <div v-if="feedTracks.length" class="grid gap-3" :style="feedGridStyle">
        <article
          v-for="track in feedTracks"
          :key="track.id"
          :class="tileClasses"
          :data-testid="track.isLocal ? 'self-video-preview' : undefined"
          :style="tileStyle"
        >
          <MediaTrackAttachment
            :track="track"
            :class="track.kind === 'screenshare' ? 'h-full w-full bg-black object-contain' : 'h-full w-full object-cover'"
          />
          <div class="pointer-events-none absolute inset-x-0 top-0 flex justify-start p-2">
            <span :class="labelPillClasses">
              <span class="truncate">{{ track.label }}</span>
            </span>
          </div>
        </article>
      </div>

      <div v-else :class="emptyStateClasses">
        {{ emptyStateLabel }}
      </div>
    </div>
  </section>
</template>
